import React, { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";

import { explore1Img, explore2Img, exploreVideo } from "../utils";
import { animateWithGsap } from "../utils/animations";

// GSAP eklentilerini React ortamında güvenle kullanmak için kaydediyoruz
gsap.registerPlugin(ScrollTrigger);

const Features = () => {
  // Tüm bileşeni kapsayacak referans (GSAP scope için)
  const containerRef = useRef(null);
  // Video elementini kontrol edecek referans
  const videoRef = useRef(null);

  useGSAP(
    () => {
      // Scroll animasyonu ile videoyu tetikleme
      gsap.to(videoRef.current, {
        scrollTrigger: {
          trigger: videoRef.current,
          toggleActions: "play pause reverse restart",
          start: "-10% bottom",
        },
        onComplete: () => {
          // Tarayıcıların autoplay kısıtlamalarına karşı Promise yapısı ile güvenli oynatma
          videoRef.current?.play().catch((error) => {
            console.warn(
              "Video otomatik oynatılamadı, kullanıcı etkileşimi bekleniyor:",
              error,
            );
          });
        },
      });

      // Custom utils üzerinden çağrılan animasyonlar
      // Not: animateWithGsap fonksiyonunuzun class ve id'leri bu component içinde
      // bulduğundan emin olmak için scope özelliğini kullanıyoruz.
      animateWithGsap("#features_title", { y: 0, opacity: 1 });

      animateWithGsap(
        ".g_grow",
        { scale: 1, opacity: 1, ease: "power1" },
        { scrub: 5.5 },
      );

      animateWithGsap(".g_text", {
        y: 0,
        opacity: 1,
        ease: "power2.inOut",
        duration: 1,
      });
    },
    { scope: containerRef },
  ); // Kapsamı bu bileşenle sınırlandırıyoruz (React Best Practice)

  return (
    <section
      ref={containerRef}
      className="h-full common-padding bg-zinc relative overflow-hidden"
    >
      {/* Typo düzeltildi: screen-max-wdith -> screen-max-width */}
      <div className="screen-max-width">
        {/* Başlık Bölümü */}
        <div className="mb-12 w-full">
          <h1 id="features_title" className="section-heading">
            Explore the full story.
          </h1>
        </div>

        {/* İçerik Bölümü */}
        <div className="flex flex-col justify-center items-center overflow-hidden">
          <div className="mt-32 mb-24 pl-24">
            <h2 className="text-5xl lg:text-7xl font-semibold">iPhone.</h2>
            <h2 className="text-5xl lg:text-7xl font-semibold">
              Forged in titanium.
            </h2>
          </div>

          <div className="flex-center flex-col sm:px-10">
            {/* Video Alanı */}
            <div className="relative h-[50vh] w-full flex items-center">
              <video
                id="exploreVideo"
                ref={videoRef}
                playsInline
                className="w-full h-full object-cover object-center"
                preload="none"
                muted
                autoPlay
                aria-label="iPhone 15 Pro titanium exploration video"
              >
                <source src={exploreVideo} type="video/mp4" />
                {/* Fallback metin eklemek her zaman profesyonel bir adımdır */}
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Özellikler ve Görseller Alanı */}
            <div className="flex flex-col w-full relative">
              <div className="feature-video-container">
                <div className="overflow-hidden flex-1 h-[50vh]">
                  <img
                    src={explore1Img}
                    alt="Close up of titanium texture"
                    className="feature-video g_grow"
                  />
                </div>
                <div className="overflow-hidden flex-1 h-[50vh]">
                  <img
                    src={explore2Img}
                    alt="Titanium frame edge details"
                    className="feature-video g_grow"
                  />
                </div>
              </div>

              {/* Açıklama Metinleri */}
              <div className="feature-text-container">
                <div className="flex-1 flex-center">
                  <p className="feature-text g_text">
                    iPhone 15 Pro is{" "}
                    <span className="text-white">
                      the first iPhone to feature an aerospace-grade titanium
                      design
                    </span>
                    , using the same alloy that spacecrafts use for missions to
                    Mars.
                  </p>
                </div>

                <div className="flex-1 flex-center">
                  <p className="feature-text g_text">
                    Titanium has one of the best strength-to-weight ratios of
                    any metal, making these our{" "}
                    <span className="text-white">
                      lightest Pro models ever.
                    </span>
                    You'll notice the difference the moment you pick one up.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
